import type { Country } from '@heidi/schema/template-gallery';
import { skipToken, useQuery } from '@tanstack/react-query';

import { ehrQueryKeys } from '../../contracts/queryKeys';
import type { IntegrationProviderOption } from '../../contracts/types';
import { getIntegrationProviders } from '../api/integration-providers';

/**
 * The country-scoped EHR/EMR provider list
 * (`release-scribe-country-specific-ehr-selection` surfaces). Headless, the
 * caller gates with `enabled` (flag resolved app-side) and supplies the
 * country. Sorted FE-side by display name; server order carries no display
 * meaning. A country change is a new cache key with no placeholder
 * ride-along, so a user can never pick an option invalid for the new country.
 */
export function useQueryIntegrationProviders({
  country,
  enabled = true,
}: {
  country: string | undefined;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ehrQueryKeys.integrationProviders(country),
    // A per-country catalogue: remounts and refocus reuse the cache; a
    // country change is a different key and fetches fresh.
    staleTime: Infinity,
    queryFn:
      enabled && country
        ? async (): Promise<IntegrationProviderOption[]> => {
            // Resolved app-side from the user/form country; the schema narrows
            // the wire param to the ISO alpha-2 Country enum.
            const providers = await getIntegrationProviders(country as Country);
            return [...providers].sort((a, b) => a.name.localeCompare(b.name));
          }
        : skipToken,
  });
}
